import { Item, Collection, timesQty } from "./collection_base"
import DataLoader from "#lib/data_loader"
import { computeEV } from "#lib/armor_ev"
import { computeSP } from "#lib/armor_sp"

// Armor — a JSON array, ordered. Coverage is stored form-natively: one hidden named
// checkbox per location (toggled by clicking the cell). The item folds those into
// covers[] + derived sp_<loc> (= baseSP for covered locations), and answers SP at a
// location — a peer can ask armorCollection.get(slug).spAt("head").

const LOCATIONS = ["head", "torso", "rarm", "larm", "rleg", "lleg"]

export class ArmorItem extends Item {
  // Fill self (edit): the flat fields + hard/skinweave bools, then expand the stored
  // covers[] into the per-location coverage checkboxes. Moved here from ArmorCollection#
  // fillItem — same body, on the item via this.builder(). (entry() re-derives sp_<loc>,
  // so the stored sp_ keys are ignored on fill.)
  async fill(entry) {
    const b = this.builder()
    b.fill(entry, {
      fields: ["name", "baseSP", "ev", "cost", "notes"],
      bools: ["isHard", "isSkinWeave"],
    })
    const covers = new Set(Array.isArray(entry.covers) ? entry.covers : [])
    for (const loc of LOCATIONS) b.bool(loc, covers.has(loc))
    // weight + isIntegral are stash-only (no editable control on the armor template — integral
    // pieces are cyberware-spawned, not authored here; weight is a fixed catalog property). The
    // layer sorter + EV calc read them; entry() serializes them. set() writes the stash and
    // no-ops the (absent) DOM binding — same pattern as baseSP on the show card.
    this.set("weight", entry.weight)
    this.set("isIntegral", !!entry.isIntegral)
  }

  // Fill self (show) — a lean worn card DISPLAYS only name + the worn toggle, but the item
  // must still be QUERYABLE for its SP (the armor-state readout asks spAt()/spByLocation()
  // over the active show items). The show template has no coverage grid / baseSP control, so
  // there's nothing to BIND — we stash the SP data DIRECTLY via set() (an unbound key is a
  // stash-only write; paintBinding no-ops). Same per-loc bool + baseSP shape as edit's fill,
  // minus the control binding — so spAt()/entry() read identically in both modes.
  async fillShow(entry) {
    this.builder().text("name", entry.name || "(unknown)")
    this.set("baseSP", entry.baseSP)
    const covers = new Set(Array.isArray(entry.covers) ? entry.covers : [])
    for (const loc of LOCATIONS) this.set(loc, covers.has(loc))
    // stash-only, same as fill() — the sorter/EV read these; show has no controls for them.
    this.set("weight", entry.weight)
    this.set("isIntegral", !!entry.isIntegral)
    this.set("isHard", !!entry.isHard)
    this.set("isSkinWeave", !!entry.isSkinWeave)
  }

  // Blob entry from the STASH: the stored scalar fields (as stashed, no numeric coercion),
  // the two flag BOOLS (coerced — checkbox-managed), and the per-location coverage folded
  // into covers[] + derived sp_<loc> (= baseSP for covered locations; never stored, always
  // re-derived so editing baseSP can't leave a stale sp_ behind). Template-independent.
  entry() {
    const e = {
      slug: this.slugValue || "",
      id: this.get("id"),
      name: this.get("name"),
      baseSP: this.get("baseSP"),
      ev: this.get("ev"),
      cost: this.get("cost"),
      notes: this.get("notes"),
      isHard: !!this.get("isHard"),
      isSkinWeave: !!this.get("isSkinWeave"),
      isIntegral: !!this.get("isIntegral"),
      weight: this.get("weight"),
    }
    const baseSP = this.baseSP()
    const covers = []
    for (const loc of LOCATIONS) {
      if (this.get(loc)) {
        covers.push(loc)
        e[`sp_${loc}`] = baseSP
      }
    }
    e.covers = covers
    return e
  }

  cost() { return timesQty({ cost: this.get("cost"), qty: this.get("qty") }) }

  // SP at a body location: baseSP when covered, else 0.
  spAt(loc) {
    return this.get(loc) ? this.baseSP() : 0
  }

  baseSP() {
    return parseInt(this.get("baseSP"), 10) || 0
  }

  // The piece as the ArmorLayerSorter / EV calc want it: sp + native ev + weight + the three
  // position flags + the locations it covers. Decoupled from the item's DOM stash so the
  // math libs stay Stimulus-free.
  layerPiece() {
    return {
      sp: this.baseSP(),
      ev: parseInt(this.get("ev"), 10) || 0,
      weight: this.get("weight"),
      isHard: !!this.get("isHard"),
      isSkinWeave: !!this.get("isSkinWeave"),
      isIntegral: !!this.get("isIntegral"),
      locations: LOCATIONS.filter((loc) => this.get(loc)),
    }
  }

  // The piece's stable id (its play-state key — armor is id-keyed, unlike chips/slug).
  itemId() { return this.get("id") }

  // --- play-state (active = worn) — the show-page toggle checkbox. NOT serialized by
  // this item (it's unnamed/unbound, so the stash + entry() never see it); the play-state controller
  // reads it via the armor collection and writes it to `state.armor`. The collection
  // PAINTS active on the DOM directly (applyActive census), so no item-level setter —
  // the item only ANSWERS isActive() and announces toggles. ---
  activeEl() { return this.element.querySelector("[data-active-toggle]") }
  isActive() { return !!this.activeEl()?.checked }

  // The user toggled worn/not-worn (show page). Announce so the play-state controller
  // re-serializes `state.armor`. Does NOT touch the item's own equipment serialize.
  toggleActive() {
    this.dispatch("play-state-changed", { prefix: "cp2020", bubbles: true })
  }
}

export class ArmorCollection extends Collection {
  static CATALOGS = ["armor"]

  // Announce (after hydrate — items rendered) so the play-state controller can PUSH the
  // persisted worn-state onto our checkboxes (applyActive). Armor's own readiness depends
  // only on its items existing, not on the state controller.
  connect() {
    super.connect()
    this._hydrated?.then(() => this.dispatch("armor-ready", { prefix: "cp2020", bubbles: true }))
  }

  // A fresh armor entry from the catalog. The catalog now carries covers[] directly (armor.json
  // was reshaped from a locations{loc:sp} object to a covers[] array — SP is uniform per piece
  // and held by baseSP, so the per-location SP was redundant). So this is a straight pick.
  async newEntry(slug) {
    const c = (await DataLoader.systemGet("armor"))[slug]
    if (!c) return null
    return {
      slug, name: c.name, baseSP: c.baseSP, ev: c.ev, cost: c.cost, notes: "",
      isHard: !!c.isHard, isSkinWeave: !!c.isSkinWeave, covers: c.covers || [],
      weight: c.weight, isIntegral: !!c.isIntegral,
    }
  }

  // Total SP at a location across all worn armor.
  spAt(loc) {
    return this.getAll().reduce((sum, i) => sum + i.spAt(loc), 0)
  }

  // Effective SP by body location over the ACTIVE (worn) pieces — the real CP2020 stacking
  // (NOT a sum; a proximity-bonus fold, inside-out; see #lib/armor_sp). Returns
  // { byLocation: { head, torso, … }, invalid: false } normally, or
  // { byLocation: {}, invalid: true } when the loadout is illegal (the sorter refuses — same
  // whole-loadout invalid rule as ev()). The state readout renders the invalid case as "—".
  spByLocation() {
    const pieces = this.getAll().filter((i) => i.isActive()).map((i) => i.layerPiece())
    return computeSP(pieces)
  }

  // Total EV (encumbrance) over the ACTIVE pieces — the real CP2020 math (native ev sum +
  // per-zone layering + CB4 over-layer; see #lib/armor_ev). Positive magnitude, bigger = worse.
  // Returns { ev: Number, invalid: false } normally, or { ev: null, invalid: true } when the
  // loadout is illegal (too many layers / >1 innermost / >1 hard — the sorter refuses, so EV
  // can't be computed). The state readout renders the invalid case distinctly.
  ev() {
    const pieces = this.getAll().filter((i) => i.isActive()).map((i) => i.layerPiece())
    return computeEV(pieces)
  }

  // --- play-state interface (the play-state controller composes this collection) -------
  // `state.armor` shape: { "<id>": { id, active, …later: degradation/damage } } — a
  // per-piece object keyed by id (richer than chips' flat active-list; armor pieces carry
  // condition, not just membership). Phase 1 populates only { id, active }.

  // PUSH-ON-LOAD: the state controller hands us the persisted per-id state; paint each
  // item's worn checkbox from its entry's `active`.
  //
  // AWAITS OWN READINESS FIRST (gate A). applyActive is called on several triggers — outlet-
  // connect (items not rendered yet), armor-ready, chips-ready — and only some coincide with a
  // hydrated set. Rather than make each caller remember to gate (and rather than a fragile
  // "did I paint anything" check), the method refuses to act until its own items are hydrated
  // + filled: you cannot call it too early. On the armor-ready path this await is already
  // resolved (that signal fires post-hydrate — gate C), so it's zero-cost there; it only
  // actually waits on the early paths. DOM census (item id + active-toggle), not getAll() —
  // even post-await we read the DOM directly (uniform with the toggle census). Idempotent.
  async applyActive(armorState) {
    await this.whenReady()
    const state = armorState || {}
    const idAttr = `data-${this.itemIdentity}-slug-value` // items also carry a hidden id input
    for (const el of this.element.querySelectorAll(`[${idAttr}]`)) {
      const idInput = el.querySelector("input[name='id']")
      const box = el.querySelector("[data-active-toggle]")
      if (idInput && box) box.checked = !!state[idInput.value]?.active
    }
    // Announce that active state was (re)applied — the SP-by-location readout derives FROM
    // which pieces are worn, so it must recompute now. The collection owns this signal (the
    // play-state controller just called us); applyActive paints the boxes SILENTLY (no native
    // events), so without this announcement a read-only consumer has no way to know worn-state
    // landed on load. Fires post-readiness (the await above), so it always reflects a settled
    // set. @window: the readout is a sibling subtree, not an ancestor.
    this.dispatch("active-applied", { prefix: "cp2020", bubbles: true, target: window })
  }

  // PULL-ON-SAVE: the state controller asks for the current per-id play-state (after a
  // toggle). Phase 1: { id, active } per piece. Active-list is DERIVED from this (a piece
  // is worn iff its object says active) — never stored as a separate list.
  activeState() {
    const out = {}
    for (const item of this.getAll()) {
      const id = item.itemId()
      if (id) out[id] = { id, active: item.isActive() }
    }
    return out
  }
}
